import { DiscountProvider } from "@redotech/cockroachdb-codegen/congodb/discount-provider";
import { DiscountGroupType } from "@redotech/redo-discounts-common/discount";
import {
  DiscountCategory,
  DiscountSettingsType,
} from "@redotech/redo-discounts-common/discount/discount-db-parser";
import {
  generatedDiscountCodePurpose,
  generatedDiscountCodeSource,
} from "@redotech/redo-model/generated-discount-code-definition";
import { AdvancedFilterDataSchema } from "@redotech/redo-model/views/views";
import { SortDirection } from "@redotech/redo-views-common/table";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

export const discountManagementSortKeys = [
  "title",
  "createdAt",
  "type",
  "code",
] as const;
export type DiscountManagementSortKey =
  (typeof discountManagementSortKeys)[number];

export const discountManagementFilterNames = {
  type: "type",
  category: "category",
  createdAt: "createdAt",
} as const;

export const generatedDiscountCodeManagementSortKeys = [
  "discountCode",
  "createdAt",
  "expiresAt",
  "usedAt",
] as const;
export type GeneratedDiscountCodeManagementSortKey =
  (typeof generatedDiscountCodeManagementSortKeys)[number];

export const generatedDiscountCodeStatusFilters = [
  "all",
  "active",
  "expired",
  "preview",
] as const;
export type GeneratedDiscountCodeStatusFilter =
  (typeof generatedDiscountCodeStatusFilters)[number];

export const generatedDiscountCodeFilterNames = { status: "status" } as const;

export const discountManagementType = [
  "percentage_off",
  "amount_off",
  "free_shipping",
  "buy_x_get_y",
  "dynamic_range",
  "unknown",
] as const;
export type DiscountManagementType = (typeof discountManagementType)[number];

export const generatedDiscountCodeLifecycleStatus = [
  "active",
  "expired",
  "preview",
] as const;
export type GeneratedDiscountCodeLifecycleStatus =
  (typeof generatedDiscountCodeLifecycleStatus)[number];

export const generatedDiscountCodeUsageStatus = [
  "seen_used",
  "not_seen_used",
] as const;
export type GeneratedDiscountCodeUsageStatus =
  (typeof generatedDiscountCodeUsageStatus)[number];

export const discountManagementSummarySchema = z.object({
  _id: zExt.objectId(),
  name: z.string(),
  provider: createEnum(DiscountProvider),
  providerIdentifier: z.string(),
  code: z.string(),
  groupType: createEnum(DiscountGroupType),
  settingsType: createEnum(DiscountSettingsType).optional(),
  type: z.enum(discountManagementType),
  category: createEnum(DiscountCategory).optional(),
  createdAt: z.date(),
  shopifyAdminUrl: z.string().optional(),
});
export type DiscountManagementSummary = z.infer<
  typeof discountManagementSummarySchema
>;

export const generatedDiscountCodeCustomerSchema = z.object({
  _id: zExt.objectId(),
  name: z.string().optional(),
});
export type GeneratedDiscountCodeCustomer = z.infer<
  typeof generatedDiscountCodeCustomerSchema
>;

export const generatedDiscountCodeManagementRowSchema = z.object({
  _id: zExt.objectId(),
  discountCode: z.string(),
  source: z.enum(generatedDiscountCodeSource),
  sourceLabel: z.string(),
  purpose: z.enum(generatedDiscountCodePurpose),
  purposeLabel: z.string(),
  customer: generatedDiscountCodeCustomerSchema.optional(),
  createdAt: z.date(),
  expiresAt: z.date().optional(),
  externalId: z.string().optional(),
  externalSource: z.string().optional(),
  shopifyAdminUrl: z.string().optional(),
  lifecycleStatus: z.enum(generatedDiscountCodeLifecycleStatus),
  usageStatus: z.enum(generatedDiscountCodeUsageStatus),
  usageCount: z.number(),
  usedAt: z.date().optional(),
  usedOnOrderId: z.string().optional(),
});
export type GeneratedDiscountCodeManagementRow = z.infer<
  typeof generatedDiscountCodeManagementRowSchema
>;

export const getDiscountManagementDetail = {
  name: "getDiscountManagementDetail" as const,
  input: z.object({ discountId: zExt.objectId() }),
  output: discountManagementSummarySchema,
};

export const paginateDiscountManagementDiscounts = {
  name: "paginateDiscountManagementDiscounts" as const,
  input: z.object({
    page: z.number().int().positive().default(1),
    pageSize: z.number().int().positive().max(100).default(50),
    sortKey: z.enum(discountManagementSortKeys).default("createdAt"),
    sortDirection: createEnum(SortDirection).default(SortDirection.DESC),
    searchString: z.string().optional(),
    filters: z.array(AdvancedFilterDataSchema).optional(),
  }),
  output: z.object({
    discounts: z.array(discountManagementSummarySchema),
    totalCount: z.number(),
  }),
};

export const paginateGeneratedDiscountCodes = {
  name: "paginateGeneratedDiscountCodes" as const,
  input: z.object({
    discountId: zExt.objectId(),
    page: z.number().int().positive().default(1),
    pageSize: z.number().int().positive().max(100).default(50),
    sortKey: z
      .enum(generatedDiscountCodeManagementSortKeys)
      .default("createdAt"),
    sortDirection: createEnum(SortDirection).default(SortDirection.DESC),
    searchString: z.string().optional(),
    statusFilter: z.enum(generatedDiscountCodeStatusFilters).default("all"),
  }),
  output: z.object({
    codes: z.array(generatedDiscountCodeManagementRowSchema),
    totalCount: z.number(),
  }),
};

// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export {
  generatedDiscountCodePurpose,
  type GeneratedDiscountCodePurpose,
} from "@redotech/redo-model/generated-discount-code-definition";
